<template>
  <div
    class="similar-products d-flex flex-column px-4 pb-4 primary"
    v-if="loadedSimilarProducts?.length > 0"
    style="width: 100%; height: 100%; max-height: 336px"
  >
    <div>
      <v-carousel
        hide-delimiters
        :show-arrows="productChunks.length > 1"
        height="max-content"
        v-model="carouselIndex"
        @change="onCarouselChange"
      >
        <v-carousel-item v-for="(chunk, index) in productChunks" :key="index">
          <v-sheet
            height="100%"
            tile
            class="d-flex align-center justify-center"
          >
            <v-row style="width: 100%" class="primary">
              <v-col
                v-for="product in chunk"
                :key="product.id"
                :cols="
                  $vuetify.breakpoint.xs
                    ? 12
                    : $vuetify.breakpoint.sm
                    ? 4
                    : $vuetify.breakpoint.md
                    ? 3
                    : 2
                "
              >
                <ProductCard
                  :product="product"
                  @select="$emit('select', $event)"
                  @add-to-cart="$emit('add-to-cart', $event)"
                  type="similarProducts"
                />
              </v-col>
            </v-row>
          </v-sheet>
        </v-carousel-item>
      </v-carousel>
    </div>
  </div>
</template>

<script>
import { Storage } from "aws-amplify";
import ProductCard from "@/components/global-components/ProductCard.vue";

export default {
  name: "SimilarProductsCarousel",
  components: {
    ProductCard,
  },
  props: {
    selectedProduct: Object,
    syncedProductCategoryArray: Array,
    similarProducts: Array,
  },
  data: () => ({
    loadedSimilarProducts: [],
    carouselIndex: 0, // ← new
    loadedProductIds: new Set(), // ← new: track which products already have thumbnails
    itemsPerPage: 4,
    productThumbnailKeys: new Map(), // id → JSON string of sorted thumbnail keys
  }),

  computed: {
    productChunks() {
      const chunks = [];
      for (
        let i = 0;
        i < this.loadedSimilarProducts.length;
        i += this.itemsPerPage
      ) {
        chunks.push(this.loadedSimilarProducts.slice(i, i + this.itemsPerPage));
      }
      return chunks;
    },

    // Currently visible products (array of product objects)
    visibleProducts() {
      if (!this.productChunks.length) return [];
      return this.productChunks[this.carouselIndex] || [];
    },
  },

  methods: {
    // Helper: get stable string representation of thumbnail keys
    getThumbnailKeySignature(product) {
      if (!product.thumbnails || product.thumbnails.length === 0) return "";
      return JSON.stringify([...product.thumbnails].sort());
    },

    // Update loaded products incrementally instead of full reset
    async updateLoadedProducts(newSimilarProducts) {
      const newMap = new Map(newSimilarProducts.map((p) => [p.id, p]));

      // Detect products whose thumbnail keys have changed (to force reload thumbnails + clear loaded flag)
      const changedThumbnailIds = [];
      for (const newP of newSimilarProducts) {
        const oldP = this.loadedSimilarProducts.find((o) => o.id === newP.id);
        if (
          oldP &&
          this.getThumbnailKeySignature(oldP) !==
            this.getThumbnailKeySignature(newP)
        ) {
          changedThumbnailIds.push(newP.id);
        }
      }
      changedThumbnailIds.forEach((id) => this.loadedProductIds.delete(id));

      // Keep products that still exist AND have unchanged thumbnail keys (safe to preserve thumbnailImages)
      const updatedLoaded = this.loadedSimilarProducts.filter((oldProduct) => {
        const newProduct = newMap.get(oldProduct.id);
        if (!newProduct) return false; // removed

        const oldSignature = this.getThumbnailKeySignature(oldProduct);
        const newSignature = this.getThumbnailKeySignature(newProduct);

        return oldSignature === newSignature;
      });

      const keptMap = new Map(updatedLoaded.map((p) => [p.id, p]));

      // Rebuild loadedSimilarProducts:
      // - For unchanged products → use new product data + preserved thumbnailImages
      // - For new or thumbnail-changed products → use new product data + empty thumbnailImages (will load later)
      this.loadedSimilarProducts = newSimilarProducts.map((newP) => {
        const kept = keptMap.get(newP.id);
        if (kept) {
          return {
            ...newP, // fresh properties (e.g. recommendedDiscount)
            thumbnailImages: kept.thumbnailImages, // preserved loaded images
          };
        } else {
          return {
            ...newP,
            thumbnailImages: [],
          };
        }
      });

      // Update thumbnail signature tracking with the new signatures
      newSimilarProducts.forEach((p) => {
        this.productThumbnailKeys.set(p.id, this.getThumbnailKeySignature(p));
      });

      // Clean up tracking for fully removed products
      for (const id of [...this.productThumbnailKeys.keys()]) {
        if (!newMap.has(id)) {
          this.productThumbnailKeys.delete(id);
          this.loadedProductIds.delete(id);
        }
      }

      // Load thumbnails for newly visible products that aren't loaded yet
      await this.$nextTick();
      this.loadVisibleThumbnails();
    },

    async loadVisibleThumbnails() {
      const productsToLoad = this.visibleProducts.filter(
        (p) => !this.loadedProductIds.has(p.id)
      );

      if (productsToLoad.length === 0) return;

      // console.log(
      //   `Loading thumbnails for ${productsToLoad.length} visible products`
      // );

      const promises = productsToLoad.map(async (product) => {
        if (product.thumbnails?.length > 0) {
          const thumbnailsToLoad = product.thumbnails.slice(0, 2);
          try {
            const thumbnailImages = await Promise.all(
              thumbnailsToLoad.map((key) =>
                Storage.get(key, { level: "public" })
              )
            );

            const index = this.loadedSimilarProducts.findIndex(
              (p) => p.id === product.id
            );
            if (index !== -1) {
              this.$set(this.loadedSimilarProducts, index, {
                ...this.loadedSimilarProducts[index],
                thumbnailImages,
              });
            }

            this.loadedProductIds.add(product.id);
          } catch (err) {
            console.warn("Failed to load thumbnail for", product.id, err);
            this.loadedProductIds.add(product.id); // prevent retry
          }
        } else {
          this.loadedProductIds.add(product.id);
        }
      });

      await Promise.all(promises);
    },

    // Called when carousel page changes + initial mount
    onCarouselChange() {
      this.$nextTick(() => {
        this.loadVisibleThumbnails();
      });
    },

    updateItemsPerPage() {
      if (this.$vuetify.breakpoint.xs) {
        this.itemsPerPage = 1; // Mobile
      } else if (this.$vuetify.breakpoint.sm) {
        this.itemsPerPage = 3; // Small screens
      } else if (this.$vuetify.breakpoint.md) {
        this.itemsPerPage = 4; // Medium screens
      } else {
        this.itemsPerPage = 6; // Large screens and above
      }
    },

    // // Reset when similar products change significantly
    // async resetAndLoadInitial() {
    //   this.loadedSimilarProducts = this.similarProducts.map((p) => ({
    //     ...p,
    //     thumbnailImages: p.thumbnailImages || [], // keep if already there
    //   }));

    //   this.loadedProductIds.clear();
    //   this.carouselIndex = 0;

    //   // Load first page
    //   await this.$nextTick();
    //   this.loadVisibleThumbnails();
    // },
  },

  mounted() {
    this.updateItemsPerPage();
  },

  watch: {
    "$vuetify.breakpoint.name": {
      handler() {
        this.updateItemsPerPage();
      },
      immediate: true,
    },

    similarProducts: {
      async handler(newVal) {
        const products = newVal || [];

        // console.log(
        //   "SimilarProductsCarousel - similarProducts changed:",
        //   products.map((p) => p.title)
        // );
        this.$emit("has-similar-products", products.length > 0);

        await this.updateLoadedProducts(products);
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
/* Ensure consistent card sizes */
.v-sheet {
  width: 100%;
}
</style>
